import React, {useEffect, useState} from "react"
import clsx from "clsx"

import ObjectID from "bson-objectid"
import {Button, ButtonGroup, CircularProgress} from "@chakra-ui/react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {IconName} from "@fortawesome/pro-regular-svg-icons"

import {Loading} from "@components/app"
import {QuestionField, Vote} from "@backend/types"
import {voteButtonsDefault} from "@backend/constants"

import {PlayCard} from "./PlayCard"
import {OutputPanel} from "./OutputPanel"
import {EndGamePanel} from "./components"
import {gamePlayApiCalls, GameStartType} from "../service"


type GameStatus = {
    status?: "gameLoading" | "textLoading" | "playing" | "endQuestion" | "endGame"
    currentQuestionPos: number,
    currentBucketPos: number,
    history: {
        [qId: string]: Vote[]
    }
    modelsBucket: [QuestionField, QuestionField][]
    remainingModels: [QuestionField, QuestionField][]
}

const voteButtons = [
    voteButtonsDefault.unsure,
    voteButtonsDefault.skip
]

const defaultGameStatus: GameStatus = {
    status: "gameLoading",
    currentQuestionPos: 0,
    currentBucketPos: 0,
    history: {},
    modelsBucket: [],
    remainingModels: []
}


export function BattleMode({
                               game: {
                                   info: {
                                       title
                                   },
                                   modeOptions: {
                                       fields, questionIds
                                   }
                               },
                               gamePlay
                           }: GameStartType) {

    const [gameStatus, setGameStatus] = useState<GameStatus>({...defaultGameStatus})

    useEffect(() => {
        const modelsBucket: [QuestionField, QuestionField][] = []

        const outputs = fields.filter(x => !x.isPinned)
        while (outputs.length) {
            const current = outputs.splice(0, 1)

            const rVal: [QuestionField, QuestionField][] = outputs.map(x => Math.random() > 0.5 ? [current[0], x] : [x, current[0]])
            modelsBucket.push(...rVal)
        }

        shuffle(modelsBucket)

        if (Object.keys(gamePlay?.voteData || {}).length) {
            // we have gameplay history

            let currentQuestionPos = 0

            const checkQuestions = questionIds.some(question => {
                if (gamePlay.voteData[question._id]) {
                    const votes = gamePlay.voteData[question._id]

                    const remainingModels = modelsBucket.filter(model => {
                        const [optionA, optionB] = model

                        return votes
                            .filter(vote => JSON.stringify(vote.modelFieldIds.sort()) === JSON.stringify([optionA._id, optionB._id].sort())).length === 0

                    })

                    if (!remainingModels.length) {
                        currentQuestionPos++
                    } else {
                        setGameStatus(prevState => ({
                            ...prevState,
                            status: "playing",
                            currentQuestionPos,
                            currentBucketPos: 0,
                            modelsBucket,
                            remainingModels
                        }))
                        return true
                    }

                } else {
                    setGameStatus(prevState => ({
                        ...prevState,
                        status: "playing",
                        currentQuestionPos,
                        currentBucketPos: 0,
                        modelsBucket,
                        remainingModels: [...modelsBucket]
                    }))
                    return true
                }
            })

            if (!checkQuestions) {
                setGameStatus(prevState => ({
                    ...prevState,
                    status: "endGame"
                }))
            }

        } else {
            setGameStatus(prevState => ({
                ...prevState,
                status: "playing",
                modelsBucket,
                remainingModels: [...modelsBucket]
            }))
        }


    }, [])

    function shuffle(array) {
        let m = array.length, t, i

        // While there remain elements to shuffle…
        while (m) {

            // Pick a remaining element…
            i = Math.floor(Math.random() * m--)

            // And swap it with the current element.
            t = array[m]
            array[m] = array[i]
            array[i] = t
        }

        return array
    }

    async function onVote(outputId: string, confidenceLevel?: string) {

        setGameStatus(prevState => ({
            ...prevState,
            status: "textLoading"
        }))

        const modelFieldIds = gameStatus.remainingModels[gameStatus.currentBucketPos].map(x => x._id)

        const voteId = ObjectID().toHexString()

        if (gamePlay?._id) {
            //save user vote by gameplayId
            const voteSaveData: Vote = {
                questionId: currentQuestion._id,
                modelFieldIds,
                vote: outputId,
                voteId,
                confidenceLevel
            }

            await gamePlayApiCalls.vote(gamePlay._id, voteSaveData).then()
        }


        const voteData: Vote = {
            questionId: currentQuestion,
            modelFieldIds,
            vote: outputId,
            voteId,
            confidenceLevel
        }


        if (gameStatus.currentBucketPos + 1 < gameStatus.remainingModels.length) {
            setGameStatus(prevState => ({
                ...prevState,
                status: "playing",
                currentBucketPos: prevState.currentBucketPos + 1,
            }))
        } else {
            if (gameStatus.currentQuestionPos + 1 < questionIds.length) {
                setGameStatus(prevState => ({
                    ...prevState,
                    status: "endQuestion"
                }))
            } else {
                return onEndGame()
            }
        }
    }

    function nextQuestion() {
        setGameStatus(prevState => ({
            ...prevState,
            status: "playing",
            currentQuestionPos: prevState.currentQuestionPos + 1,
            currentBucketPos: 0,
        }))
    }

    function onEndGame() {
        if (gamePlay?._id) {
            gamePlayApiCalls
                .endGame(gamePlay._id)
                .finally(() => {
                    setGameStatus({
                        ...defaultGameStatus,
                        status: "endGame"
                    })
                })
        }
    }

    if (gameStatus.status === "endGame")
        return <EndGamePanel
            cardTitle={`You have completed the ${title} game!`}
            title="Thanks for playing!"
        />

    if (gameStatus.status === "endQuestion")
        return <EndGamePanel
            cardTitle="Round Complete!"
            title="You have rated all the different answers to this legal question! Thank you for your hard work."
            subTitle="There are more legal questions to review. Do you want to play another round?"
            onContinue={nextQuestion}
            gamePlayId={gamePlay._id}
            questionId={questionIds[gameStatus.currentQuestionPos]._id}
        />

    if (gameStatus.status === "gameLoading")
        return <Loading/>

    const currentQuestion = questionIds[gameStatus.currentQuestionPos]
    const currentBucket = gameStatus.remainingModels[gameStatus.currentBucketPos]

    const input = currentQuestion.texts.find(x => x.optionKey === "input")?.text
    const outputA = currentQuestion.texts.find(x => x.optionKey === currentBucket[0].optionKey)?.text
    const outputB = currentQuestion.texts.find(x => x.optionKey === currentBucket[1].optionKey)?.text

    console.log("remainingModels", gameStatus.remainingModels)
    return <div className="space-y-4">
        <div className="border-b border-gray-200 p-4 flex justify-between items-center">
            <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">Which answer is better?</h3>
                <p className="mt-2 max-w-4xl text-sm text-gray-500">
                    Which answer would better help (and not harm) a person dealing with this legal problem?
                </p>
            </div>
            <CircularProgress value={(gameStatus.currentBucketPos) * (100 / gameStatus.remainingModels.length)}/>

        </div>

        <div className={clsx("grid grid-cols-1 gap-4", currentQuestion.texts.length > 1 && "md:grid-cols-2")}>

            <PlayCard title="Question"
                      className="whitespace-break-spaces prose max-w-full col-span-1 md:col-span-2"
            >
                <p>{input}</p>
            </PlayCard>

            {gameStatus.status === "textLoading" ? <Loading/> :
                <>
                    <OutputPanel
                        outputId={currentBucket[0]._id}
                        title="Answer A"
                        text={outputA}
                        btnProps={{
                            icon: voteButtonsDefault.accept.icon as IconName,
                            bgColor: `${voteButtonsDefault.accept.colorScheme}.500`,
                            text: "Option A is better"
                        }}
                        onVote={onVote}
                    />

                    <OutputPanel
                        outputId={currentBucket[1]._id}
                        title="Answer B"
                        text={outputB}
                        btnProps={{
                            icon: voteButtonsDefault.accept.icon as IconName,
                            bgColor: `${voteButtonsDefault.accept.colorScheme}.500`,
                            text: "Option B is better"
                        }}
                        onVote={onVote}
                    />
                </>
            }
        </div>

        <div className="flex justify-end items-center space-x-4">
            <ButtonGroup size="sm">
                {voteButtons.map((voteBtn) => (
                    <Button
                        key={voteBtn.key}
                        isLoading={["btnLoading", "textLoading"].includes(gameStatus.status)}
                        leftIcon={<FontAwesomeIcon icon={["fal", voteBtn.icon as IconName]}/>}
                        className="col-span-1"
                        bg={`${voteBtn.colorScheme}.500`}
                        color="white"
                        _hover={{opacity: "0.8"}}
                        onClick={onVote.bind(null, voteBtn.key, null)}
                        size="lg">
                        {voteBtn.text}
                    </Button>
                ))}
            </ButtonGroup>
        </div>
    </div>
}